import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Loader2, ArrowLeft, Clock, ChefHat, Wrench, AlertTriangle, ChevronRight, Play } from "lucide-react";

const RecipeSkillDetail = () => {
  const { slug } = useParams();
  const [skill, setSkill] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/skills/${slug}`).then((r) => setSkill(r.data)).catch(() => setSkill(null)).finally(() => setLoading(false));
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [slug]);

  useEffect(() => {
    if (!skill) return;
    if (skill.image_status !== "pending") return;
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts += 1;
      try {
        const r = await api.get(`/skills/${slug}/image_status`);
        if (r.data.status === "ready" && r.data.image_data) {
          setSkill((s) => ({ ...s, image_data: r.data.image_data, image_status: "ready" }));
          clearInterval(pollRef.current);
        } else if (r.data.status === "failed" || attempts > 30) {
          setSkill((s) => ({ ...s, image_status: "failed" }));
          clearInterval(pollRef.current);
        }
      } catch { if (attempts > 30) clearInterval(pollRef.current); }
    }, 4000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [skill?.slug, skill?.image_status]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-stone-950">
      <Loader2 className="w-6 h-6 animate-spin text-orange-600" />
    </div>
  );

  if (!skill) return (
    <div className="min-h-screen flex items-center justify-center bg-stone-950 pt-24 px-6">
      <div className="text-center">
        <div className="magazine-number text-9xl mb-6">Ø</div>
        <p className="font-serif-display italic text-2xl text-stone-400 mb-8">This lesson has walked off.</p>
        <Link to="/skills" className="text-orange-500 uppercase text-xs tracking-[0.24em] ink-link">Back to skills</Link>
      </div>
    </div>
  );

  const heroImg = skill.image_data ? `data:image/png;base64,${skill.image_data}` : null;

  return (
    <div className="min-h-screen pt-24 pb-32 bg-stone-950">
      <div className="max-w-[1200px] mx-auto px-6 sm:px-10">
        <Link to="/skills" data-testid="skill-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-stone-500 hover:text-orange-500 mb-10 ink-link">
          <ArrowLeft className="w-3.5 h-3.5" /> All skills
        </Link>

        <header className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-20">
          <div className="lg:col-span-6">
            <div className="eyebrow mb-6 text-orange-500">{skill.category.replace('-', ' ')}</div>
            <h1 data-testid="skill-title" className="font-serif-display text-4xl sm:text-5xl md:text-6xl text-stone-100 leading-[0.95] sm:leading-[0.9] tracking-tight sm:tracking-tighter break-words">
              {skill.title}
            </h1>
            <p className="mt-6 font-serif-display italic text-2xl text-orange-500">{skill.subtitle}</p>
            <p className="mt-8 text-stone-300 leading-relaxed text-lg">{skill.intro}</p>
            <div className="mt-8 flex flex-wrap gap-6 text-sm text-stone-400 font-mono-accent">
              <span className="flex items-center gap-2"><Clock className="w-4 h-4" />~{skill.duration_min} min</span>
              <span className="flex items-center gap-2"><ChefHat className="w-4 h-4" />{skill.difficulty}</span>
            </div>
          </div>
          <div className="lg:col-span-6">
            {heroImg ? (
              <img src={heroImg} alt={skill.title} className="w-full aspect-[4/3] object-cover" />
            ) : (
              <div className="w-full aspect-[4/3] bg-gradient-to-br from-orange-950 via-stone-900 to-stone-950 border border-stone-800 flex flex-col items-center justify-center gap-3">
                {skill.image_status === "pending" && <Loader2 className="w-6 h-6 animate-spin text-orange-600/60" />}
                <span className="font-serif-display italic text-lg text-stone-400">Illustration on its way</span>
              </div>
            )}
          </div>
        </header>

        <div className="grid lg:grid-cols-12 gap-16">
          <aside className="lg:col-span-4 lg:sticky lg:top-24 lg:self-start space-y-10">
            {skill.tools_needed?.length > 0 && (
              <div className="border-t border-stone-800 pt-6">
                <div className="flex items-center gap-3 mb-6">
                  <Wrench className="w-4 h-4 text-orange-600" />
                  <h2 className="eyebrow">What you need</h2>
                </div>
                <ul className="space-y-3">
                  {skill.tools_needed.map((t, i) => (
                    <li key={i} className="flex gap-3 text-stone-300">
                      <ChevronRight className="w-3.5 h-3.5 mt-1.5 text-orange-600 shrink-0" />
                      <span className="text-sm leading-relaxed">{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {skill.watch_outs?.length > 0 && (
              <div className="border border-orange-900/40 bg-orange-950/10 p-5">
                <div className="flex items-center gap-3 mb-4">
                  <AlertTriangle className="w-4 h-4 text-orange-500" />
                  <span className="eyebrow text-orange-500">Watch out</span>
                </div>
                <ul className="space-y-3">
                  {skill.watch_outs.map((w, i) => (
                    <li key={i} className="text-sm text-stone-300 leading-relaxed">
                      &ndash; {w}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </aside>

          <div className="lg:col-span-8">
            {skill.video_id && (
              <section data-testid="skill-video" className="mb-16 border-t border-stone-800 pt-6">
                <div className="flex items-center gap-3 mb-6">
                  <Play className="w-4 h-4 text-orange-600" fill="currentColor" />
                  <h2 className="eyebrow">Watch a demonstration</h2>
                </div>
                <div className="aspect-video w-full bg-stone-900 border border-stone-800">
                  <iframe
                    className="w-full h-full"
                    src={`https://www.youtube-nocookie.com/embed/${skill.video_id}?rel=0&modestbranding=1`}
                    title={skill.video_title || skill.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
                <div className="mt-4 flex flex-wrap items-baseline gap-x-4 gap-y-1">
                  <p className="font-serif-display italic text-lg text-stone-200">
                    {skill.video_title}
                  </p>
                  {skill.video_credit && (
                    <p className="text-xs uppercase tracking-[0.24em] text-orange-500">
                      hosted by {skill.video_credit}
                    </p>
                  )}
                </div>
                <a
                  href={`https://www.youtube.com/watch?v=${skill.video_id}`}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="skill-video-external"
                  className="mt-2 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-stone-500 hover:text-orange-500 ink-link"
                >
                  Watch on YouTube
                  <ChevronRight className="w-3 h-3" />
                </a>
              </section>
            )}

            <div className="flex items-center gap-3 mb-10 border-t border-stone-800 pt-6">
              <ChefHat className="w-4 h-4 text-orange-600" />
              <h2 className="eyebrow">How to do it</h2>
            </div>
            <ol className="space-y-14">
              {skill.steps.map((step, i) => (
                <li key={i} data-testid={`skill-step-${i}`} className="grid grid-cols-[80px_1fr] gap-6 sm:gap-10">
                  <div className="magazine-number text-6xl sm:text-7xl">{String(step.number).padStart(2, "0")}</div>
                  <div>
                    <h3 className="font-serif-display text-2xl text-stone-100 mb-3">{step.title}</h3>
                    <p className="text-stone-300 leading-relaxed text-lg">{step.body}</p>
                    {step.duration_min ? (
                      <div className="mt-3 text-xs text-stone-500 font-mono-accent uppercase tracking-widest">
                        ~{step.duration_min} min
                      </div>
                    ) : null}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecipeSkillDetail;
